<template>
    <div class="live-results-page">
        <!-- Main Display Area -->
        <div class="main-display">
            <!-- Countdown Mode -->
            <div v-if="mode === 'countdown'" class="countdown-section">
                <div class="countdown-container">
                    <h1 class="next-draw-title">{{ nextDraw.gameName || 'LUCKIA LOTTERY' }}</h1>
                    <div class="game-info">
                        <h2 class="game-name">{{ getTranslatedText('Next Draw') }}</h2>
                        <p class="draw-name">{{ nextDraw.drawName }}</p>
                    </div>
                    <div class="countdown-timer">
                        <div class="time-block">
                            <span class="time-value">{{ countdown.hours }}</span>
                            <span class="time-label">{{ getTranslatedText('Hours') }}</span>
                        </div>
                        <span class="time-separator">:</span>
                        <div class="time-block">
                            <span class="time-value">{{ countdown.minutes }}</span>
                            <span class="time-label">{{ getTranslatedText('Minutes') }}</span>
                        </div>
                        <span class="time-separator">:</span>
                        <div class="time-block">
                            <span class="time-value">{{ countdown.seconds }}</span>
                            <span class="time-label">{{ getTranslatedText('Seconds') }}</span>
                        </div>
                    </div>
                    <p class="draw-time">{{ nextDraw.drawDate }}</p>
                </div>
            </div>

            <!-- Animation Mode -->
            <div v-if="mode === 'animation'" class="animation-section">
                <iframe 
                    ref="animationFrame"
                    :src="animationUrl" 
                    frameborder="0" 
                    class="animation-iframe"
                    @load="onAnimationLoad"
                ></iframe>
            </div>
        </div>

        <!-- Last 3 Draws Section -->
        <div class="recent-draws-section">
            <h3 class="recent-draws-title">{{ getTranslatedText('Last 3 Draws') }}</h3>
            <div class="recent-draws-list">
                <div v-for="(draw, index) in recentDraws" :key="index" class="draw-item">
                    <div class="draw-info">
                        <span class="draw-game">{{ draw.gameName }}</span>
                        <span class="draw-name">{{ draw.drawName }}</span>
                        <span class="draw-date">{{ draw.drawDate }}</span>
                    </div>
                    <div class="draw-numbers-container">
                        <div v-for="(row, rowIndex) in draw.rows" :key="'row-' + rowIndex" class="draw-numbers-row">
                            <span v-for="(num, i) in row.mainNumbers" :key="'main-' + i" class="number-ball">{{ num }}</span>
                            <span v-if="row.bonusNumbers && row.bonusNumbers.length > 0" class="number-plus">+</span>
                            <span v-for="(num, i) in row.bonusNumbers" :key="'bonus-' + i" class="number-ball bonus">{{ num }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import languageMixins from '@/mixins/language/language-mixins'
import GamesService from '@/services/games.service'
import ResultsService from '@/services/results.service'
import { getParamQueryTheme, isStrEmpty } from '@/utils/utils'
import { setAccount } from '@/services/axios'

const POLL_INTERVAL = 3 * 60 * 1000 // 3 minutes in milliseconds
const POLL_INTERVAL_AFTER_DRAW = 10 * 1000 // 10 seconds when waiting for results after draw time
const ANIMATION_DURATION = 60 * 1000 // 60 seconds (adjust based on your animation length)

export default {
    name: 'LiveResults',
    mixins: [languageMixins],
    data() {
        return {
            mode: 'countdown', // 'countdown' or 'animation'
            nextDraw: {
                eventId: null,
                gameSlug: '',
                gameName: '',
                drawName: '',
                drawDate: '',
                drawDateTime: null
            },
            countdown: {
                hours: '00',
                minutes: '00',
                seconds: '00'
            },
            recentDraws: [],
            animationUrl: '',
            pollingTimer: null,
            countdownTimer: null,
            animationTimer: null,
            lastCheckedEventId: null,
            isWaitingForResults: false,
            waitingForResultsTimer: null
        }
    },
    mounted() {
        // Set account from URL params if available (for public access without token)
        const accountFromUrl = getParamQueryTheme('account')
        if (!isStrEmpty(accountFromUrl)) {
            setAccount(accountFromUrl)
        }
        this.initialize()
    },
    beforeDestroy() {
        this.cleanup()
    },
    methods: {
        async initialize() {
            await this.fetchNextDraw()
            await this.fetchRecentDraws()
            this.startCountdown()
            this.startPolling()
            // Check immediately for any results (don't wait 3 minutes)
            await this.checkForNewResults()
        },

        cleanup() {
            if (this.pollingTimer) clearInterval(this.pollingTimer)
            if (this.countdownTimer) clearInterval(this.countdownTimer)
            if (this.animationTimer) clearTimeout(this.animationTimer)
            if (this.waitingForResultsTimer) clearInterval(this.waitingForResultsTimer)
        },

        async fetchNextDraw() {
            // Don't fetch next draw if we're waiting for results or showing animation
            if (this.isWaitingForResults || this.mode === 'animation') {
                console.log('Skipping fetchNextDraw - waiting for results or animation in progress')
                return
            }
            
            try {
                // Fetch upcoming events for multi-lotto games
                // This API returns ALL upcoming events for both 5/90 and 4/59 games
                const response = await GamesService.getListEvents('multi-lotto')
                
                console.log('Fetching next draw - Response:', response)
                
                if (response.code === 200 && response.data && response.data.length > 0) {
                    // Find the next draw across all games (both 5/90 and 4/59)
                    let nextEvent = null
                    let nextEventTime = null

                    response.data.forEach(item => {
                        const eventTime = new Date(item.draw_date)
                        console.log(`Checking event: ${item.game} at ${item.draw_date}`, eventTime)
                        
                        // Only consider future draws
                        if (eventTime > new Date()) {
                            // Find the earliest draw (whichever comes first)
                            if (!nextEventTime || eventTime < nextEventTime) {
                                const gameSlug = item.game?.toLowerCase() || ''
                                const is459 = gameSlug.includes('4') && gameSlug.includes('59')
                                const is590 = gameSlug.includes('5') && gameSlug.includes('90')
                                
                                // Construct proper game name based on game type
                                let gameName = 'LUCKIA LOTTERY'
                                if (is459) {
                                    gameName = 'Lucky 4/59'
                                } else if (is590) {
                                    gameName = 'Lucky 5/90'
                                }
                                
                                nextEventTime = eventTime
                                nextEvent = {
                                    eventId: item.event_id,
                                    gameSlug: gameSlug,
                                    gameName: gameName,
                                    drawName: item.event_name || item.draw_name || 'Draw',
                                    drawDate: this.formatDrawDate(item.draw_date),
                                    drawDateTime: eventTime
                                }
                                console.log('New next event selected:', nextEvent)
                            }
                        }
                    })

                    if (nextEvent) {
                        this.nextDraw = nextEvent
                        console.log('Next draw set to:', this.nextDraw)
                    } else {
                        console.log('No future draws found')
                    }
                }
            } catch (error) {
                console.error('Error fetching next draw:', error)
            }
        },

        async fetchRecentDraws() {
            try {
                const date = new Date().toISOString().split('T')[0]
                
                // Fetch results from API for multi-lotto
                const response = await ResultsService.listResults(date, 'multi-lotto')
                
                console.log('Recent draws response:', response)
                
                if (response.code === 200 && response.data && response.data.length > 0) {
                    this.recentDraws = response.data.slice(0, 3).map(item => {
                        const gameSlug = item.game?.toLowerCase() || ''
                        const is459 = gameSlug.includes('4') && gameSlug.includes('59')
                        
                        let mainNumbers = []
                        let bonusNumbers = []
                        
                        let rows = []
                        
                        if (is459) {
                            // For 4/59: Just 4 main balls in one row, no bonus
                            mainNumbers = item.results && item.results[0] ? item.results[0] : []
                            rows = [
                                { mainNumbers: mainNumbers, bonusNumbers: [] }
                            ]
                        } else {
                            // For 5/90: 2 rows
                            // Row 1: First 5 balls + 2 bonus balls
                            const firstFive = item.results && item.results[0] ? item.results[0] : []
                            const bonusTwo = item.results && item.results[1] ? item.results[1] : []
                            
                            // Row 2: Last 5 balls + 2 joker balls (from double_chance_results)
                            let lastFive = []
                            let jokerTwo = []
                            
                            if (item.double_chance_results && item.double_chance_results.length > 0) {
                                lastFive = item.double_chance_results[0] || []
                                jokerTwo = item.double_chance_results[1] || []
                            }
                            
                            rows = [
                                { mainNumbers: firstFive, bonusNumbers: bonusTwo },
                                { mainNumbers: lastFive, bonusNumbers: jokerTwo }
                            ]
                        }

                        return {
                            gameName: item.game || (is459 ? 'Lucky 4/59' : 'Lucky 5/90'),
                            drawName: item.event_name || item.draw_name || 'Draw',
                            drawDate: this.formatDrawDate(item.draw_date),
                            rows: rows
                        }
                    })
                    console.log('Recent draws formatted:', this.recentDraws)
                }
            } catch (error) {
                console.error('Error fetching recent draws:', error)
            }
        },

        async checkForNewResults() {
            if (!this.nextDraw.eventId) {
                console.log('No next draw event ID to check')
                return
            }

            try {
                console.log(`Polling for results... Looking for event_id: ${this.nextDraw.eventId}`)
                const date = new Date().toISOString().split('T')[0]
                
                const response = await ResultsService.listResults(date, 'multi-lotto')
                
                if (response.code === 200 && response.data && response.data.length > 0) {
                    console.log('Latest results:', response.data)
                    
                    // Check all recent results for our expected event
                    const matchingResult = response.data.find(result => result.event_id === this.nextDraw.eventId)
                    
                    if (matchingResult) {
                        console.log('Found matching result for expected event:', matchingResult)
                        
                        // Stop waiting for results since we found them
                        this.stopWaitingForResults()
                        
                        // Check if this is a new result we haven't seen yet
                        if (matchingResult.event_id !== this.lastCheckedEventId) {
                            console.log('New result detected! Playing animation...')
                            this.lastCheckedEventId = matchingResult.event_id
                            await this.playAnimation(matchingResult)
                        } else {
                            console.log('Result already processed')
                        }
                    } else {
                        console.log('No matching result found yet - event_id:', this.nextDraw.eventId)
                        console.log('Available results event_ids:', response.data.map(r => r.event_id))
                        // If we're waiting for results (countdown reached 0), keep checking
                        if (this.isWaitingForResults && !this.waitingForResultsTimer) {
                            console.log('Starting frequent polling for results...')
                            this.startWaitingForResults()
                        }
                    }
                } else {
                    // No results data yet, keep checking if waiting
                    console.log('No results data returned from API')
                    if (this.isWaitingForResults && !this.waitingForResultsTimer) {
                        console.log('Starting frequent polling for results (no data)...')
                        this.startWaitingForResults()
                    }
                }
            } catch (error) {
                console.error('Error checking for new results:', error)
            }
        },

        async playAnimation(resultData) {
            console.log('Playing animation for result:', resultData)
            // Stop countdown timer while showing animation
            if (this.countdownTimer) {
                clearInterval(this.countdownTimer)
                this.countdownTimer = null
            }
            this.mode = 'animation'
            
            const gameSlug = resultData.game?.toLowerCase() || ''
            const is459 = gameSlug.includes('4') && gameSlug.includes('59')
            
            console.log(`Game type detected: ${is459 ? '4/59' : '5/90'}`)
            
            // Build animation URL with results data
            const baseUrl = window.location.origin
            const drawDate = new Date(resultData.draw_date)
            const formattedDate = drawDate.toLocaleDateString('fr-FR', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            }).toUpperCase()

            if (is459) {
                // For 4/59
                const numbers = resultData.results && resultData.results[0] ? resultData.results[0] : []
                const params = {
                    results: encodeURIComponent(JSON.stringify({ numbers })),
                    date: formattedDate
                }
                const queryString = Object.keys(params).map(key => `${key}=${params[key]}`).join('&')
                this.animationUrl = `${baseUrl}/draw-animation-459.html?${queryString}`
                console.log('4/59 Animation URL:', this.animationUrl)
            } else {
                // For 5/90
                const firstFive = resultData.results && resultData.results[0] ? resultData.results[0] : []
                const bonusTwo = resultData.results && resultData.results[1] ? resultData.results[1] : []
                const lastFive = resultData.double_chance_results && resultData.double_chance_results[0] ? resultData.double_chance_results[0] : []
                const jokerTwo = resultData.double_chance_results && resultData.double_chance_results[1] ? resultData.double_chance_results[1] : []
                
                const params = {
                    results: encodeURIComponent(JSON.stringify({ firstFive, bonusTwo, lastFive, jokerTwo })),
                    eventName: resultData.event_name || resultData.draw_name || 'Le Big',
                    date: formattedDate
                }
                const queryString = Object.keys(params).map(key => `${key}=${params[key]}`).join('&')
                this.animationUrl = `${baseUrl}/draw-animation.html?${queryString}`
                console.log('5/90 Animation URL:', this.animationUrl)
            }

            // Return to countdown after animation duration
            this.animationTimer = setTimeout(async () => {
                console.log('Animation duration complete, returning to countdown')
                this.mode = 'countdown'
                this.stopWaitingForResults()
                await this.fetchNextDraw()
                await this.fetchRecentDraws()
                // Restart countdown timer for the new draw
                this.startCountdown()
                // Check immediately if there's another result available
                await this.checkForNewResults()
            }, ANIMATION_DURATION)
        },

        onAnimationLoad() {
            // Animation iframe loaded
            console.log('Animation loaded')
        },

        startCountdown() {
            // Clear any existing countdown timer before starting a new one
            if (this.countdownTimer) {
                clearInterval(this.countdownTimer)
                this.countdownTimer = null
            }
            this.updateCountdown()
            this.countdownTimer = setInterval(() => {
                this.updateCountdown()
            }, 1000)
        },

        async updateCountdown() {
            // Don't update if we're waiting for results or showing animation
            if (this.isWaitingForResults || this.mode === 'animation') {
                return
            }
            
            if (!this.nextDraw.drawDateTime) {
                this.countdown = { hours: '00', minutes: '00', seconds: '00' }
                return
            }

            const now = new Date()
            const diff = this.nextDraw.drawDateTime - now

            if (diff <= 0) {
                this.countdown = { hours: '00', minutes: '00', seconds: '00' }
                // Draw time has passed, check for results and play animation if available
                if (!this.isWaitingForResults) {
                    console.log('Countdown reached 0, checking for results...')
                    this.isWaitingForResults = true
                    // Stop countdown timer while waiting for results
                    if (this.countdownTimer) {
                        clearInterval(this.countdownTimer)
                        this.countdownTimer = null
                    }
                    // Check immediately for results
                    await this.checkForNewResults()
                    // If no results found, start frequent polling
                    if (this.isWaitingForResults && !this.waitingForResultsTimer) {
                        this.startWaitingForResults()
                    }
                }
                // Don't update countdown or fetch next draw - wait for results or animation to complete
                return
            }

            const hours = Math.floor(diff / (1000 * 60 * 60))
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
            const seconds = Math.floor((diff % (1000 * 60)) / 1000)

            this.countdown = {
                hours: String(hours).padStart(2, '0'),
                minutes: String(minutes).padStart(2, '0'),
                seconds: String(seconds).padStart(2, '0')
            }
        },

        startWaitingForResults() {
            // Check more frequently when waiting for results after draw time
            console.log('Starting frequent polling for results...')
            this.waitingForResultsTimer = setInterval(() => {
                this.checkForNewResults()
            }, POLL_INTERVAL_AFTER_DRAW)
        },

        stopWaitingForResults() {
            if (this.waitingForResultsTimer) {
                clearInterval(this.waitingForResultsTimer)
                this.waitingForResultsTimer = null
                console.log('Stopped waiting for results')
            }
            this.isWaitingForResults = false
        },

        startPolling() {
            this.pollingTimer = setInterval(() => {
                // Only poll if not waiting for results or showing animation
                if (!this.isWaitingForResults && this.mode !== 'animation') {
                    this.checkForNewResults()
                }
            }, POLL_INTERVAL)
        },

        formatDrawDate(dateString) {
            const date = new Date(dateString)
            return date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        }
    }
}
</script>

<style lang="scss" scoped>
.live-results-page {
    min-height: 100vh;
    background: linear-gradient(135deg, #FF6B00 0%, #FFA500 100%);
    display: flex;
    flex-direction: column;
}

.main-display {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
}

.countdown-section {
    width: 100%;
    max-width: 800px;
}

.countdown-container {
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border-radius: 20px;
    padding: 3rem;
    text-align: center;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.next-draw-title {
    font-size: 2rem;
    color: white;
    margin-bottom: 1rem;
    font-weight: 700;
    text-transform: uppercase;
}

.game-info {
    margin-bottom: 2rem;
}

.game-name {
    font-size: 3rem;
    color: #ffd700;
    font-weight: 900;
    margin-bottom: 0.5rem;
}

.draw-name {
    font-size: 3rem;
    color: white;
    font-weight: 900;
    text-transform: uppercase;
    margin-top: 1rem;
}

.countdown-timer {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    margin: 2rem 0;
}

.time-block {
    display: flex;
    flex-direction: column;
    align-items: center;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 10px;
    padding: 1.5rem;
    min-width: 100px;
}

.time-value {
    font-size: 3.5rem;
    font-weight: 900;
    color: white;
    line-height: 1;
}

.time-label {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.8);
    text-transform: uppercase;
    margin-top: 0.5rem;
}

.time-separator {
    font-size: 3rem;
    color: white;
    font-weight: bold;
}

.draw-time {
    font-size: 1.2rem;
    color: rgba(255, 255, 255, 0.8);
    margin-top: 1rem;
}

.animation-section {
    width: 100%;
    height: 100%;
}

.animation-iframe {
    width: 100%;
    height: 80vh;
    border-radius: 10px;
}

.recent-draws-section {
    background: rgba(0, 0, 0, 0.3);
    padding: 2rem;
    backdrop-filter: blur(10px);
}

.recent-draws-title {
    color: white;
    font-size: 1.5rem;
    font-weight: 700;
    text-align: center;
    margin-bottom: 1.5rem;
    text-transform: uppercase;
}

.recent-draws-list {
    display: flex;
    gap: 1.5rem;
    justify-content: center;
    flex-wrap: wrap;
}

.draw-item {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
    padding: 1.5rem;
    min-width: 300px;
    backdrop-filter: blur(5px);
}

.draw-info {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-bottom: 1rem;
}

.draw-game {
    font-size: 1.2rem;
    font-weight: 700;
    color: #ffd700;
}

.draw-name {
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.9);
}

.draw-date {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.7);
}

.draw-numbers-container {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.draw-numbers-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    justify-content: center;
    align-items: center;
}

.number-ball {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #FF6B00 0%, #FFA500 100%);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 1.1rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.number-ball.bonus {
    background: linear-gradient(135deg, #8B4513 0%, #D2691E 100%);
}

.number-plus {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    font-weight: 900;
    color: white;
    padding: 0 0.5rem;
}

@media (max-width: 768px) {
    .countdown-timer {
        flex-direction: row;
        gap: 0.5rem;
    }

    .time-block {
        min-width: 80px;
        padding: 1rem;
    }

    .time-value {
        font-size: 2.5rem;
    }

    .game-name {
        font-size: 2rem;
    }

    .recent-draws-list {
        flex-direction: column;
    }
}
</style>

